import Tippy, { useSingleton } from "@tippyjs/react";
import cx from "classnames";
import { useAtomValue } from "jotai";
import type { ReactNode } from "react";
import { canv } from "@/canv/global";
import { MakeRequest } from "@/canv/utils";
import { generateColor } from "@/helpers/generateColor";
import { getInitials } from "@/helpers/getInitials";
import { CommentsIcon, MultiplayerCursorsIcon } from "@/icons";
import {
    boardInfoAtom,
    headerActiveButtonAtom,
    multiCursorsAtom,
    usersAtom,
} from "@/interface/AppStore";
import { BottomBarItem } from "@/interface/BottomBar";
import { Tooltip } from "@/interface/ui/Tooltip";
import { Button } from "@/shared/ui/Button";
import { isMobile } from "@/utils";
import styles from "./RightBar.module.scss";
import { Timer } from "./Timer";
import { Vote } from "./Vote";

type RightBarProps = {
    className?: string;
};

export const RightBar = ({ className }: RightBarProps) => {
    const [source, target] = useSingleton();
    const { disableMultiCursors } = useAtomValue(multiCursorsAtom);
    const { boardAccessToken, boardMode } = useAtomValue(boardInfoAtom);
    const { users, trackingSession } = useAtomValue(usersAtom);
    const activeButtonName = useAtomValue(headerActiveButtonAtom);

    const showTimer = !canv.isBoardModeView();
    const showShareButton =
        boardAccessToken === "" && (boardMode === "edit" || boardMode === "owner");
    const showVote = canv.isBoardModeEditOrOwner() && canv.isTeamMember();

    const showMultiLimit = isMobile() ? 2 : 4;
    const tryView = (sessionUID: string, e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        canv.multiManager.TryViewSubscribe(sessionUID);
    };
    const renderUsers = () => {
        const userMonets: ReactNode[] = [];

        for (let i = 0; i < users.length && i < showMultiLimit; i++) {
            const isCurrentUser = canv.getInitDataProp("userToken") === users[i].token;
            userMonets.push(
                <div
                    key={users[i].sessionUID}
                    className={styles.barPanelRightUsersItem}
                    onClick={(e) => tryView(users[i].sessionUID, e)}
                >
                    <div
                        className={cx(
                            styles.barPanelRightUsersItemView,
                            trackingSession === users[i].sessionUID &&
                                styles.barPanelRightUsersItemIndicatorSelected,
                        )}
                        style={{
                            backgroundColor: generateColor(
                                isCurrentUser
                                    ? users[i].sessionUID
                                    : users[i].token || users[i].sessionUID,
                            ),
                        }}
                    >
                        {getInitials(users[i].name)}
                    </div>
                </div>,
            );
        }

        userMonets.push(
            <div key={canv.getInitDataProp("userToken")} className={styles.barPanelRightUsersItem}>
                <div
                    className={styles.barPanelRightUsersItemView}
                    style={{
                        backgroundColor: generateColor(canv.getInitDataProp("userToken")),
                    }}
                >
                    {getInitials(canv.getInitDataProp("userName"))}
                </div>
            </div>,
        );

        return userMonets;
    };

    const openUsersWindow = () => {
        canv.appInterface.usersWindow.show();
    };

    const handleShareButtonClick = () => {
        // TODO: переделать shareWindow на реакт
        canv.appInterface.shareWindow.open({
            mode: "board",
            token: canv.getInitDataProp("userToken"),
            accessSelector: canv.appInterface.accessMenu.open.bind(canv.appInterface.accessMenu),
            host: canv.host,
            HTTPRequest: MakeRequest,
            stateChange: () => {},
            notificator: canv.appInterface.infoWindow.open.bind(canv.appInterface.infoWindow),
            searchInputState: (state) => {
                canv.appInterface.inputFocused = state;
            },

            boardUID: canv.getInitDataProp("boardUID"),
            onlyRead: canv.getInitDataProp("onlyRead"),
        });
    };

    return (
        <div className={className}>
            <Tippy singleton={source} />
            <div className={styles.actions}>
                {showTimer && <Timer singletoneTarget={target} />}
                {showVote && <Vote singletoneTarget={target} />}
                {/* TODO: comments */}
                <Tooltip singleton={target} content="Список комментариев">
                    <BottomBarItem
                        active={activeButtonName === "comments"}
                        onClick={() => {
                            canv.appInterface.commentsWindow.open();
                        }}
                    >
                        <CommentsIcon />
                    </BottomBarItem>
                </Tooltip>
                <Tooltip
                    singleton={target}
                    content={disableMultiCursors ? "Показать курсоры" : "Скрыть курсоры"}
                >
                    <BottomBarItem
                        active={!disableMultiCursors}
                        onClick={() => {
                            canv.multiManager.setDisableMultiCursors(!disableMultiCursors);
                        }}
                    >
                        <MultiplayerCursorsIcon />
                    </BottomBarItem>
                </Tooltip>

                <BottomBarItem onClick={openUsersWindow}>
                    <span className={styles.barPanelRightUsers}>{renderUsers()}</span>
                    {users.length > showMultiLimit && (
                        <div className="barPanelRightUsersItemViewMore">
                            +{users.length - showMultiLimit}
                        </div>
                    )}
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M12.7082 15.7068C12.5204 15.8945 12.2656 16 12 16C11.7344 16 11.4796 15.8945 11.2918 15.7068L5.28121 9.6974C5.09873 9.5085 4.99776 9.2555 5.00004 8.99286C5.00232 8.73026 5.10768 8.47905 5.29341 8.29335C5.47914 8.10765 5.7304 8.00232 5.99306 8.00004C6.25571 7.99776 6.50876 8.09871 6.69769 8.28115L12 13.5824L17.3023 8.28115C17.4912 8.09871 17.7443 7.99776 18.0069 8.00004C18.2696 8.00232 18.5209 8.10765 18.7066 8.29335C18.8923 8.47905 18.9977 8.73026 19 8.99286C19.0022 9.2555 18.9013 9.5085 18.7188 9.6974L12.7082 15.7068Z"
                            fill="#2C2D32"
                        />
                    </svg>
                </BottomBarItem>
            </div>
            {/* TODO: share */}
            {showShareButton && (
                <Button onClick={handleShareButtonClick} size="m">
                    Поделиться
                </Button>
            )}
        </div>
    );
};
